import { FC, useMemo } from 'react';
import { SubmitButton } from '@/components/form/submit-button';
import { Button } from '@/components/ui/button';
import { strings } from '@/constants/strings';
import { useForm } from '@/forms/hooks/use-form';
import { AssociateDeviceFieldValues } from '@/forms/schemas/associate-device/field-values';
import { buildAssociateDeviceFormSchema } from '@/forms/schemas/associate-device/schema';
import { useUnitSelectOptions } from '@/hooks/use-unit-select-options';
import { DrawerContentFormLayout } from '@/layouts/drawer-content-form-layout';
import { MaybeAssociatedDevice } from '@/types/device';
import { Unit } from '@/types/unit';

export type AssociateDevicesFormProps = {
  devices: MaybeAssociatedDevice[];
  units: Unit[];
  onCancel: () => void;
  onSubmit: (values: AssociateDeviceFieldValues) => void;
};

export const AssociateDevicesForm: FC<AssociateDevicesFormProps> = ({
  devices,
  units,
  onCancel,
  onSubmit,
}) => {
  const unitOptions = useUnitSelectOptions(units);

  const currentUnitDatabaseId = useMemo(() => {
    const firstDeviceUnitId = devices[0]?.unit?.id;
    const allDevicesHaveSameUnit = devices.every(
      (device) => device.unit?.id === firstDeviceUnitId,
    );
    return allDevicesHaveSameUnit ? firstDeviceUnitId : undefined;
  }, [devices]);

  const formSchema = useMemo(
    () =>
      buildAssociateDeviceFormSchema({
        currentUnitDatabaseId,
        unitOptions,
      }),
    [currentUnitDatabaseId, unitOptions],
  );

  const { fieldItems, formState, handleSubmit } = useForm({
    schema: formSchema,
    onSubmit,
  });

  return (
    <DrawerContentFormLayout
      buttonItems={[
        <Button key="cancel" onClick={onCancel} variant="secondary">
          {strings.devices.drawer_associate.cancel}
        </Button>,
        <SubmitButton
          disabled={formState.isSubmitting}
          key="submit"
          text={strings.devices.drawer_associate.submit}
        />,
      ]}
      fieldItems={fieldItems}
      loading={formState.isSubmitting}
      onSubmit={handleSubmit}
    />
  );
};
